import { QrScanner } from '@yudiel/react-qr-scanner';
import { useEffect, useState } from 'react';

import { TProductCategory, TProductInstance } from '@lib/core/products/types';
import { parseError } from '@lib/core/service/requests/parseError';
import { checkIfScannedCodeIsEan } from '@lib/core/service/slices/technical/scanner';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { localeWidget } from '@lib/tools/locale/source/web/widget';
import { LocaleUtils } from '@lib/tools/locale/utils';
import { getProductPlaceholderImage } from '@lib/tools/shared/helpers';
import { VH_VARIANTS } from '@lib/tools/shared/helpers/consts';

import Button from '@components/web/src/atoms/Buttons/Button';
import { CloseButton } from '@components/web/src/atoms/Buttons/CloseButton/CloseButton';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS, RETAILER_COLORS } from '@components/web/src/styles';
import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';
import { CameraModeSwitcher } from '@components/web/src/templates/Tutorials/ScannerBarcode/components/CameraModeSwitcher';
import * as S from '@components/web/src/templates/Tutorials/ScannerBarcode/styles';

export type TCameraFacingMode = 'environment' | 'user';

type BaseProps = {
  productCategory: TProductCategory;
  isEnableKioskFidelityScanAddon?: boolean;
  handleClose: () => void;
  handleProductPageNavigation: (productId: string) => void;
  handleFidelityCode: (fidelityCardCode: string, productCategory: TProductCategory) => void;
};

type Props = BaseProps &
  (
    | {
        isFreeFormTastingAddon?: false;
        handleSearchCatalogNavigation?: () => void;
        handleSelectGameProduct?: (product: TProductInstance) => void;
      }
    | {
        isFreeFormTastingAddon: true;
        handleSearchCatalogNavigation: () => void;
        handleSelectGameProduct: (product: TProductInstance) => void;
      }
  );

export const ScannerBarcode = ({
  productCategory,
  isEnableKioskFidelityScanAddon = false,
  isFreeFormTastingAddon = false,
  handleClose,
  handleProductPageNavigation,
  handleFidelityCode,
  handleSearchCatalogNavigation,
  handleSelectGameProduct,
}: Props) => {
  const {
    scanBarcodeText,
    scanBarcodeAndFidelityText,
    notValidCodeErrorText,
    permissionDeniedErrorText,
    searchManuallyText,
  } = localeWidget.scanBarcode;

  const productCategoryText = LocaleUtils.publishedTerms[localeCommon?.productCategories[productCategory].id];
  const bannerText = isEnableKioskFidelityScanAddon ? scanBarcodeAndFidelityText : scanBarcodeText;

  const [cameraFacingMode, setCameraFacingMode] = useState<TCameraFacingMode>('environment');
  const [isScanning, setIsScanning] = useState(false);
  const [requestErrorMessage, setRequestErrorMessage] = useState(null);
  const [scannerErrorMessage, setScannerErrorMessage] = useState(null);
  const errorMessage = requestErrorMessage || scannerErrorMessage;

  useEffect(() => {
    if (!requestErrorMessage) return () => null;
    const timer = setTimeout(() => setRequestErrorMessage(null), 2000);
    return () => clearTimeout(timer);
  }, [requestErrorMessage]);

  const handleRequestError = (code: string, errorText) => {
    MixpanelTracker.events.scanFailed(code);
    setRequestErrorMessage(errorText);
  };

  const handleBarcodeScan = async (scannedCode: string) => {
    if (scannedCode.length < 6 || scannedCode.length > 13) {
      handleRequestError(scannedCode, notValidCodeErrorText);
      return;
    }

    setIsScanning(true);

    try {
      const { isScannedCodeEan, productData } = await checkIfScannedCodeIsEan(scannedCode);

      if (isFreeFormTastingAddon && isScannedCodeEan && handleSelectGameProduct) {
        MixpanelTracker.events.scanProduct(productData, scannedCode);
        handleSelectGameProduct(productData);
        handleClose();
        return;
      }

      if (isScannedCodeEan) {
        MixpanelTracker.events.scanProduct(productData, scannedCode);
        handleProductPageNavigation(productData?.identifier);
        handleClose();
        return;
      }

      if (isEnableKioskFidelityScanAddon) {
        await handleFidelityCode(scannedCode, productCategory);
        handleClose();
        return;
      }

      MixpanelTracker.events.scanFailed(scannedCode);
      handleRequestError(scannedCode, notValidCodeErrorText);
    } catch (error) {
      handleRequestError(scannedCode, parseError(error).message);
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <ModalWrapper
      isAnimated
      isOpen
      withoutDefaultPadding
      isTransparent={false}
      minModalContentHeight="100%"
      modalContentHeight="100%"
      onClose={handleClose}
    >
      <Flexbox isFullHeight direction="column" gap={0}>
        <S.ContentWrapper isFullWidth direction="column">
          <CloseButton handleClick={handleClose} type="bold" />
          <S.ScannerWrapper>
            <QrScanner
              audio={false}
              constraints={{ facingMode: cameraFacingMode }}
              containerStyle={{ borderRadius: '20px' }}
              onDecode={scannedCode => {
                if (!isScanning && !errorMessage) {
                  handleBarcodeScan(scannedCode);
                }
              }}
              onError={error =>
                setScannerErrorMessage(error?.name === 'NotAllowedError' ? permissionDeniedErrorText : error?.name)
              }
            />
            <S.CustomValidatorIcon $isError={!!errorMessage} />
          </S.ScannerWrapper>
          {errorMessage && <Text color={COLORS.base.black} size="subtitle2" text={errorMessage} textAlign="center" />}
          {!scannerErrorMessage && (
            <Flexbox isFullWidth justify="center">
              <CameraModeSwitcher cameraFacingMode={cameraFacingMode} onChange={setCameraFacingMode} />
            </Flexbox>
          )}
          {isFreeFormTastingAddon && handleSearchCatalogNavigation && (
            <Button
              fontSize="body1"
              iconPosition="right"
              iconVariant="search"
              size="sm"
              text={searchManuallyText}
              textColor={COLORS.base.black}
              textDecoration="underline"
              textWeight="medium"
              variant={VH_VARIANTS.LINK}
              handleClick={() => {
                handleSearchCatalogNavigation();
                handleClose();
              }}
            />
          )}
        </S.ContentWrapper>
        <S.BannerWrapper isFullWidth align="center" gap={0}>
          <S.ProductImage alt={`${productCategory} image`} src={getProductPlaceholderImage(productCategory)} />
          <Text
            color={RETAILER_COLORS.primary[700]}
            localeIndex={{ productCategory }}
            localeOptions={{ productCategoryText }}
            size="subtitle2"
            text={bannerText}
            weight="semibold"
          />
        </S.BannerWrapper>
      </Flexbox>
    </ModalWrapper>
  );
};
